import { createReducer, on } from '@ngrx/store';

import { SurveyCreationActions } from '../audience/survey-creation-audience.actions';
import {
  INITIAL_INSIGHTS_STATE,
  INITIAL_SURVEY_CREATION_PRICING_STATE,
} from './survey-creation-insights-state.constants';
import { InsightsState } from './survey-creation-insights-state.model';
import {
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
} from './survey-creation-insights.actions';

export const insightsReducer = createReducer(
  INITIAL_INSIGHTS_STATE,

  on(
    SurveyCreationInsightsApiActions.computeScorePending,
    (state): InsightsState => ({
      ...state,
      score: {
        ...state.score,
        loading: true,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.computeScoreSuccess,
    (state, { scorePercentage }): InsightsState => ({
      ...state,
      score: {
        scorePercentage,
        loading: false,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.computeScoreFailure,
    (state, { error }): InsightsState => ({
      ...state,
      score: {
        scorePercentage: null,
        loading: false,
        error,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilityPending,
    (state): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        loading: true,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilitySuccess,
    (
      state,
      { total, quotaGroups, audience, hasQuota, cpi, suppliers },
    ): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        total,
        quotaGroups,
        audience,
        hasQuota,
        cpi,
        suppliers,
        loading: false,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilityFailure,
    (state, { error }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        loading: false,
        error,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilityWithExclusionsSuccess,
    (
      state,
      { total, quotaGroups, audience, hasQuota, cpi, suppliers },
    ): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        withExclusions: {
          total,
          quotaGroups,
          audience,
          hasQuota,
          cpi,
          suppliers,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilityWithExclusionsFailure,
    (state): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        withExclusions: null,
      },
    }),
  ),

  on(
    SurveyCreationInsightsActions.applyAdvancedFilters,
    (state, { filters }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFilters: filters,
      },
    }),
  ),

  on(
    SurveyCreationInsightsActions.toggleExcludeFromFeasibleAudience,
    (state, { excludeFromFeasibleAudience }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFilters: {
          ...state.feasibility.advancedFilters,
          excludeFromFeasibleAudience,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.findSurveysLoading,
    (state): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          loading: true,
          error: null,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.findSurveysSuccess,
    (state, { surveys }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          criteriaSurveySummaries: surveys,
          loading: false,
          error: null,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.findSurveysFailure,
    (state, { error }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          loading: false,
          error,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.respondentCountsSuccess,
    (state, { totalCount }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          respondentCount: totalCount,
          error: null,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.respondentCountsFailure,
    (state, { error }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          error,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.searchCriteriaSurveysLoading,
    (state): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          loading: true,
          error: null,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.searchCriteriaSurveysSuccess,
    (state, { surveys }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          criteriaSearchResults: surveys,
          loading: false,
          error: null,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.searchCriteriaSurveysFailure,
    (state, { error }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          loading: false,
          error,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.searchGroupSurveysLoading,
    (state): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          loading: true,
          error: null,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.searchGroupSurveysSuccess,
    (state, { surveys }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          groupSearchResults: surveys,
          loading: false,
          error: null,
        },
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.searchGroupSurveysFailure,
    (state, { error }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          loading: false,
          error,
        },
      },
    }),
  ),

  // Also hydrates pricing.offerPrice/surveyPauseThreshold from these prices —
  // this fires on survey load and every recalculation, so a save made without
  // ever opening the CPI drawer still carries the survey's existing pricing
  // instead of wiping it with `null`. Apply overwrites it afterwards.
  on(
    SurveyCreationInsightsApiActions.loadFlexibilityPricesSuccess,
    (state, { prices }): InsightsState => ({
      ...state,
      flexibilityPrices: {
        prices,
        error: null,
      },
      pricing: {
        ...state.pricing,
        offerPrice: prices.surveyPriceOverride,
        surveyPauseThreshold: prices.surveyPauseThreshold,
      },
    }),
  ),

  on(
    SurveyCreationInsightsActions.applyCpiPricing,
    (
      state,
      { offerPrice, surveyPauseThreshold, blendApplied },
    ): InsightsState => ({
      ...state,
      pricing: { offerPrice, surveyPauseThreshold, blendApplied },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.loadFlexibilityPricesFailure,
    (state, { error }): InsightsState => ({
      ...state,
      flexibilityPrices: {
        prices: null,
        error,
      },
      pricing: { ...INITIAL_SURVEY_CREATION_PRICING_STATE },
    }),
  ),

  on(SurveyCreationActions.reset, (): InsightsState => INITIAL_INSIGHTS_STATE),
);
